import {
	Assignment, Build, CheckCircle, ChevronRight, ErrorOutline, FilterAlt, PeopleAlt, Schedule
} from "@mui/icons-material";
import { Button, MenuItem, TextField, Typography } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/pt-br";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useAtom } from "jotai";
import { FC, Fragment, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { permissionsAtom } from "../../atoms/atom";
import AppBar from "../../components/app-bar/AppBar";
import { useAlert } from "../../components/contextProviders/AlertProvider";
import EpiBarChart from "../../components/epi/EpiBarChart";
import EpiDonut from "../../components/epi/EpiDonut";
import EpiLineChart from "../../components/epi/EpiLineChart";
import EpiStatCard from "../../components/epi/EpiStatCard";
import { EPI_COLORS, companyLabel } from "../../components/epi/epiColors";
import { EpiDashboardService } from "../../services/EpiDashboardService";
import { EpiInspectionService } from "../../services/EpiInspectionService";
import { EpiCompanyConfigDTO } from "../../types/EpiCompanyConfigDTO";
import { EpiDashboardDTO } from "../../types/EpiDashboardDTO";
import { EpiDashboardSliceDTO } from "../../types/EpiDashboardSliceDTO";
import { useUtils } from "../../utils/utilFunctions";
import { parseBrDate } from "../epi-inspection-detail/EpiInspectionDetail";
import { getLoading } from "../execution-events/ExecutionEvents";
import './EpiDashboard.scss';

const SECTOR_PALETTE = [
	EPI_COLORS.info, EPI_COLORS.purple, EPI_COLORS.orange, EPI_COLORS.warning,
	EPI_COLORS.success, EPI_COLORS.danger, EPI_COLORS.neutral
];

dayjs.extend(customParseFormat);

const EpiDashboard: FC = () => {
	const [permissions] = useAtom(permissionsAtom);
	const [companies, setCompanies] = useState<EpiCompanyConfigDTO[]>([]);
	const [companyId, setCompanyId] = useState<number | ''>('');
	const [sector, setSector] = useState('');
	const [sectorOptions, setSectorOptions] = useState<string[]>([]);
	const [from, setFrom] = useState('');
	const [to, setTo] = useState('');
	const [fromValue, setFromValue] = useState<Dayjs | null>(null);
	const [toValue, setToValue] = useState<Dayjs | null>(null);
	const [fromError, setFromError] = useState(false);
	const [toError, setToError] = useState(false);
	const [dashboard, setDashboard] = useState<EpiDashboardDTO>();
	const [loading, setLoading] = useState(false);
	const requestSeq = useRef(0);
	const lastAppliedFrom = useRef('');
	const lastAppliedTo = useRef('');
	const navigate = useNavigate();
	const { showError } = useAlert();
	const { verifyPermission } = useUtils();
	const service = new EpiDashboardService();
	const configService = new EpiInspectionService();

	useEffect(() => {
		const loadCompanies = async () => {
			const response = await configService.getCompanies();
			const enabled = (response?.data || []).filter(company => company.enabled);
			setCompanies(enabled);
			if (enabled.length === 1) {
				setCompanyId(enabled[0].companyId);
			}
		};
		loadCompanies();
		if (!!permissions.length) {
			verifyPermission(permissions, 'EPI_DASHBOARD');
		}
	}, []);

	useEffect(() => {
		setSector('');
		search_({ sector: '' });
	}, [companyId]);

	return (
		<Fragment>
			<AppBar
				modulo="Inspeção de EPIs"
			/>
			<div className="epi-dashboard">
				<div className="cabecalho">
					<Typography variant="h6">Dashboard NR-06</Typography>
				</div>
				<div className="filtros">
					<TextField
						select
						label="Empresa"
						size="small"
						value={companyId}
						onChange={(e) => setCompanyId(Number(e.target.value))}
					>
						{companies.map(company => (
							<MenuItem key={company.companyId} value={company.companyId}>
								{companyLabel(company)}
							</MenuItem>
						))}
					</TextField>
					<TextField
						select
						SelectProps={{ displayEmpty: true }}
						InputLabelProps={{ shrink: true }}
						label="Setor"
						size="small"
						value={sector}
						onChange={(e) => {
							const value = e.target.value;
							setSector(value);
							search_({ sector: value });
						}}
					>
						<MenuItem value="">Todos os setores</MenuItem>
						{sectorOptions.map(item => <MenuItem key={item} value={item}>{item}</MenuItem>)}
					</TextField>
					<LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
						<DatePicker
							label="De"
							value={fromValue}
							onChange={(newValue) => handleFromChange(newValue)}
							slotProps={{ textField: { size: 'small', error: fromError, helperText: fromError ? 'Data inválida' : undefined } }}
						/>
						<DatePicker
							label="Até"
							value={toValue}
							onChange={(newValue) => handleToChange(newValue)}
							slotProps={{ textField: { size: 'small', error: toError, helperText: toError ? 'Data inválida' : undefined } }}
						/>
					</LocalizationProvider>
					<div className="acoes-filtro">
						<Button variant="outlined" startIcon={<FilterAlt />} onClick={clearFilters}>
							LIMPAR
						</Button>
					</div>
				</div>
				{loading
					? getLoading()
					: !dashboard
						? <div className="sem-execucoes">
							<Typography variant="h6">
								Selecione uma empresa para ver o dashboard.
							</Typography>
						</div>
						: <Fragment>
							<div className="indicadores">
								<EpiStatCard
									variant="label-first"
									icon={<Assignment />}
									color={EPI_COLORS.info}
									label="Inspeções realizadas"
									value={String(dashboard.concludedInspections)}
									detail="100% do total"
								/>
								<EpiStatCard
									variant="label-first"
									icon={<CheckCircle />}
									color={EPI_COLORS.success}
									label="Conformidade geral"
									value={formatIndex(dashboard.complianceIndex)}
									detail={`Meta: ≥ ${dashboard.complianceGoal}%`}
									chipLabel={dashboard.needsAttention ? 'Atenção' : undefined}
									chipColor={EPI_COLORS.warning}
								/>
								<EpiStatCard
									variant="label-first"
									icon={<ErrorOutline />}
									color={EPI_COLORS.danger}
									label="Não conformidades"
									value={String(dashboard.totalNonConformities)}
									detail={percentOf(dashboard.openNonConformities, dashboard.totalNonConformities, 'abertas')}
								/>
								<EpiStatCard
									variant="label-first"
									icon={<PeopleAlt />}
									color={EPI_COLORS.purple}
									label="Colaboradores envolvidos"
									value={String(dashboard.involvedEmployees)}
									detail="100% do total"
								/>
								<EpiStatCard
									variant="label-first"
									icon={<Schedule />}
									color={EPI_COLORS.orange}
									label="Pendências de exames"
									value={String(dashboard.pendingExams)}
									detail={`${dashboard.overdueExams} vencidas`}
									chipLabel={dashboard.overdueExams > 0 ? 'Urgente' : undefined}
									chipColor={EPI_COLORS.danger}
								/>
							</div>
							<div className="roscas">
								<div className="painel-rosca">
									<Typography variant="h6">Conformidade por setor</Typography>
									<EpiDonut
										segments={toSegments(dashboard.complianceBySector, sectorColor)}
										total={sumValues(dashboard.complianceBySector)}
										totalLabel="Índice geral"
										centerValue={formatIndex(dashboard.complianceIndex)}
										legendValueFormatter={(segment) => {
											const slice = dashboard.complianceBySector
												.find(item => item.label === segment.label);
											return formatIndex(slice?.percentage);
										}}
									/>
									{renderLink('VER DETALHES', 'EPI_COMPLIANCE', '/inspecao-epis/conformidade')}
								</div>
								<div className="painel-rosca">
									<Typography variant="h6">Status das inspeções</Typography>
									<EpiDonut
										segments={toSegments(dashboard.inspectionCoverage, statusColor)}
										total={sumValues(dashboard.inspectionCoverage)}
										totalLabel="Total"
									/>
									{renderLink('VER TODAS INSPEÇÕES', 'EPI_INSPECTION', '/inspecao-epis/inspecoes')}
								</div>
								<div className="painel-rosca">
									<Typography variant="h6">Não conformidades por status</Typography>
									<EpiDonut
										segments={toSegments(dashboard.actionsByStatus, statusColor)}
										total={sumValues(dashboard.actionsByStatus)}
										totalLabel="Total"
									/>
									{renderLink('VER NÃO CONFORMIDADES', 'EPI_NONCONFORMITY', '/inspecao-epis/nao-conformidades')}
								</div>
							</div>
							<div className="graficos">
								<div className="painel-grafico">
									<Typography variant="h6">Evolução da conformidade (%)</Typography>
									<EpiLineChart
										labels={dashboard.complianceEvolution.map(slice => slice.label)}
										values={dashboard.complianceEvolution.map(slice =>
											slice.percentage === undefined || slice.percentage === null
												? null
												: slice.percentage)}
										goal={dashboard.complianceGoal}
										emptyMessage="Sem inspeções concluídas no período."
									/>
								</div>
								<div className="painel-grafico">
									<Typography variant="h6">Pendências de exames por status</Typography>
									<EpiBarChart
										labels={dashboard.examsByStatus.map(slice => slice.label)}
										values={dashboard.examsByStatus.map(slice => slice.value || 0)}
										percentages={dashboard.examsByStatus.map(slice => slice.percentage || 0)}
										colors={dashboard.examsByStatus.map(slice => statusColor(slice.key))}
										emptyMessage="Sem pendências de exame."
									/>
								</div>
							</div>
							<div className="rodape-cards">
								<EpiStatCard
									variant="label-first"
									icon={<Build />}
									color={EPI_COLORS.info}
									label="Ações corretivas em andamento"
									value={String(dashboard.ongoingActions)}
									detail="Aguardando conclusão"
									linkLabel={permissions.includes('EPI_NONCONFORMITY') ? 'VER PLANO DE AÇÃO' : undefined}
									onLinkClick={() => navigate('/inspecao-epis/nao-conformidades')}
								/>
								<EpiStatCard
									variant="label-first"
									icon={<CheckCircle />}
									color={EPI_COLORS.success}
									label="Ciências registradas"
									value={String(dashboard.acknowledgements)}
									detail="Neste período"
									linkLabel={permissions.includes('EPI_INSPECTION') ? 'VER CIÊNCIAS' : undefined}
									onLinkClick={() => navigate('/inspecao-epis/inspecoes')}
								/>
							</div>
						</Fragment>
				}
			</div>
		</Fragment>
	);

	async function search_(overrides?: { sector?: string, from?: string, to?: string }) {
		if (companyId === '') {
			setDashboard(undefined);
			return;
		}
		const requestedSector = overrides?.sector ?? sector;
		const requestedFrom = overrides?.from ?? from;
		const requestedTo = overrides?.to ?? to;
		const fromTime = parseBrDate(requestedFrom);
		const toTime = parseBrDate(requestedTo);
		if (fromTime !== null && toTime !== null && toTime < fromTime) {
			showError('Período inválido.');
			return;
		}
		const seq = ++requestSeq.current;
		setLoading(true);
		const response = await service.getDashboard(Number(companyId), requestedSector, requestedFrom, requestedTo);
		if (seq !== requestSeq.current) {
			return;
		}
		setDashboard(response?.data);
		if (requestedSector === '') {
			setSectorOptions(distinct((response?.data?.complianceBySector || []).map(slice => slice.label)));
		}
		setLoading(false);
	}

	function clearFilters() {
		setSector('');
		setFrom('');
		setTo('');
		setFromValue(null);
		setToValue(null);
		setFromError(false);
		setToError(false);
		lastAppliedFrom.current = '';
		lastAppliedTo.current = '';
		search_({ sector: '', from: '', to: '' });
	}

	function handleFromChange(newValue: Dayjs | null) {
		setFromValue(newValue);
		if (newValue !== null && !newValue.isValid()) {
			setFromError(true);
			return;
		}
		setFromError(false);
		const value = newValue === null ? '' : newValue.format('DD/MM/YYYY');
		if (value === lastAppliedFrom.current) {
			return;
		}
		lastAppliedFrom.current = value;
		setFrom(value);
		search_({ from: value });
	}

	function handleToChange(newValue: Dayjs | null) {
		setToValue(newValue);
		if (newValue !== null && !newValue.isValid()) {
			setToError(true);
			return;
		}
		setToError(false);
		const value = newValue === null ? '' : newValue.format('DD/MM/YYYY');
		if (value === lastAppliedTo.current) {
			return;
		}
		lastAppliedTo.current = value;
		setTo(value);
		search_({ to: value });
	}

	function renderLink(label: string, permission: string, path: string) {
		if (!permissions.includes(permission)) {
			return null;
		}
		return (
			<div className="link-rosca" onClick={() => navigate(path)}>
				{label} <ChevronRight fontSize="small" />
			</div>
		);
	}

	function toSegments(slices: EpiDashboardSliceDTO[], colorOf: (key: string, index: number) => string) {
		return slices.map((slice, index) => ({
			label: slice.label,
			value: slice.value || 0,
			color: colorOf(slice.key, index)
		}));
	}

	function sumValues(slices: EpiDashboardSliceDTO[]) {
		return slices.reduce((total, slice) => total + (slice.value || 0), 0);
	}

	function sectorColor(_key: string, index: number) {
		return SECTOR_PALETTE[index % SECTOR_PALETTE.length];
	}

	function statusColor(key: string) {
		switch (key) {
			case 'CONCLUIDAS':
			case 'CONFORME':
			case 'Concluídas':
				return EPI_COLORS.success;
			case 'ABERTAS':
			case 'VENCIDAS':
			case 'VENCIDOS':
				return EPI_COLORS.danger;
			case 'EM_ANDAMENTO':
			case 'A_VENCER':
				return EPI_COLORS.warning;
			case 'AGENDADOS':
				return EPI_COLORS.info;
			case 'PENDENTE':
			case 'NAO_APLICAVEL':
			case 'Pendente':
				return EPI_COLORS.neutral;
			default:
				return EPI_COLORS.neutral;
		}
	}

	function formatIndex(value?: number) {
		return value === undefined || value === null ? '—' : `${value.toFixed(1)}%`;
	}

	function percentOf(part: number, total: number, suffix: string) {
		return total ? `${(part * 100 / total).toFixed(0)}% ${suffix}` : `0% ${suffix}`;
	}

	function distinct(values: string[]) {
		return Array.from(new Set(values.filter(value => !!value))).sort();
	}

};

export default EpiDashboard;
